import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Loader2, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../services/supabaseClient';
import { usePageMeta } from '../hooks/usePageMeta';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Reset Password Flash-Read | Recover Account',
    description: 'Reset your Flash-Read password and recover your account. Regain access to your reading progress, achievements, and speed reading data.',
  });

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing your reset link...');

  useEffect(() => {
    // DEBUG: Log URL details to troubleshoot the issue
    console.log('=== PASSWORD RESET DEBUG ===');
    console.log('Full URL:', window.location.href);
    console.log('Hash:', window.location.hash);
    console.log('Search:', window.location.search);
    console.log('Pathname:', window.location.pathname);
    console.log('===========================');

    // Check if we have a valid session from the reset link
    const handleReset = async () => {
      if (!isSupabaseConfigured) {
        setStatus('error');
        setMessage('Supabase credentials missing. Check .env file.');
        return;
      }

      try {
        // First, check for token in URL hash (from Supabase email redirect)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (accessToken) {
          // Set the session from the URL tokens
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });

          if (sessionError) {
            setStatus('error');
            setMessage('Invalid or expired reset link.');
            return;
          }

          setStatus('loading');
          setMessage('Reset link verified. Enter your new password below.');
          return;
        }

        // Fallback: check for existing session (from cookies/localStorage)
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          setStatus('error');
          setMessage('Invalid or expired reset link.');
          return;
        }

        if (session) {
          setStatus('loading');
          setMessage('Reset link verified. Enter your new password below.');
        } else {
          setStatus('error');
          setMessage('Invalid or expired reset link. Please request a new one.');
        }
      } catch (err) {
        setStatus('error');
        setMessage('An error occurred. Please try again.');
      }
    };

    handleReset();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setMessage('Password must be at least 6 characters.');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      setStatus('success');
      setMessage('Password updated successfully! You can now sign in.');
    } catch (err: any) {
      setStatus('error');
      setMessage(err.message || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 flex flex-col items-center justify-center px-4 bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-8">
        {/* Header */}
        <div className="flex flex-col items-center mb-8">
          <div className={`p-4 rounded-full mb-4 ${
            status === 'success' ? 'bg-green-100 dark:bg-green-900/30' :
            status === 'error' ? 'bg-red-100 dark:bg-red-900/30' :
            'bg-blue-100 dark:bg-blue-900/30'
          }`}>
            {status === 'success' ? (
              <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
            ) : status === 'error' ? (
              <XCircle className="h-10 w-10 text-red-600 dark:text-red-400" />
            ) : (
              <KeyRound className="h-10 w-10 text-primary" />
            )}
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            {status === 'success' ? 'Password Updated' :
             status === 'error' ? 'Reset Link Invalid' :
             'Set New Password'}
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-2 text-center text-sm">
            {message}
          </p>
        </div>

        {/* Password Form (only show when we have a valid session) */}
        {status === 'loading' && message.includes('Enter your new password') && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* New Password Input */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">New Password</label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                <input 
                  type="password" 
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary outline-none transition-all text-slate-900 dark:text-white placeholder-slate-400"
                  placeholder="Enter new password"
                />
              </div>
            </div>

            {/* Confirm Password Input */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Confirm Password</label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                <input 
                  type="password" 
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary outline-none transition-all text-slate-900 dark:text-white placeholder-slate-400"
                  placeholder="Confirm new password"
                />
              </div>
            </div>

            {/* Error Message */}
            {message && !message.includes('Enter your new password') && (
              <div className="text-sm p-3 rounded-lg bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
                {message}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-primary hover:bg-blue-600 text-white rounded-xl font-semibold transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-blue-500/20 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <KeyRound className="w-5 h-5" />
                  <span>Update Password</span>
                </>
              )}
            </button>
          </form>
        )}

        {/* Success State */}
        {status === 'success' && (
          <button
            onClick={() => navigate('/login')}
            className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-primary hover:bg-blue-600 text-white rounded-xl font-semibold transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-blue-500/20"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Sign In</span>
          </button>
        )}

        {/* Error State */}
        {status === 'error' && (
          <button
            onClick={() => navigate('/login')}
            className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-900 dark:text-white rounded-xl font-semibold transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Sign In</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
